import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type CellAnchorComment = {
  _id?: string;
  description?: string;
  cellAnchor?: {
    sheetIndex: number;
    sheetName?: string;
    range: string;
  };
  disposition?: string;
};

/**
 * Citation target inside the workbook. Sheet is resolved by index first,
 * then by name; the cited cells come from `range` (A1 cell or range) or
 * `row` (1-based, highlights the whole row when no column is known).
 */
export type SpreadsheetCitationHighlight = {
  sheetIndex?: number;
  sheetName?: string;
  range?: string;
  row?: number;
};

export type SpreadsheetViewProps = {
  url: string;
  comments?: CellAnchorComment[];
  /**
   * Selected comment. When set, only that comment's cells are outlined
   * (mirrors the pin viewers, which show only the active pin) and they are
   * scrolled into view — stepping through comments visibly tracks selection.
   */
  activeCommentId?: string | null;
  activeSheetIndex?: number;
  highlight?: SpreadsheetCitationHighlight | null;
  onSheetChange?: (sheetIndex: number) => void;
  onCommentClick?: (commentId: string) => void;
};

type Sheet = {
  name: string;
  cells: unknown[][];
  colCount: number;
};

type ParsedWorkbook = {
  sheets: Sheet[];
};

const MAX_RENDER_ROWS = 1000;
const MAX_RENDER_COLS = 50;

function colLetter(colIndex: number): string {
  let n = colIndex + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function parseA1(ref: string): { col: number; row: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref.toUpperCase());
  if (!m) return null;
  const letters = m[1];
  const row = parseInt(m[2], 10) - 1;
  let col = 0;
  for (const ch of letters) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  return { col: col - 1, row };
}

function parseRange(
  range: string,
): { minCol: number; minRow: number; maxCol: number; maxRow: number } | null {
  const parts = range.replace(/\s+/g, "").split(":");
  if (parts.length === 1) {
    const p = parseA1(parts[0]);
    if (!p) return null;
    return { minCol: p.col, minRow: p.row, maxCol: p.col, maxRow: p.row };
  }
  const a = parseA1(parts[0]);
  const b = parseA1(parts[1]);
  if (!a || !b) return null;
  return {
    minCol: Math.min(a.col, b.col),
    minRow: Math.min(a.row, b.row),
    maxCol: Math.max(a.col, b.col),
    maxRow: Math.max(a.row, b.row),
  };
}

export function SpreadsheetView(props: SpreadsheetViewProps) {
  const { t } = useTranslation("components");
  const {
    url,
    comments,
    activeCommentId,
    activeSheetIndex,
    highlight,
    onSheetChange,
    onCommentClick,
  } = props;

  const [workbook, setWorkbook] = useState<ParsedWorkbook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localActiveSheet, setLocalActiveSheet] = useState<number>(
    activeSheetIndex ?? 0,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const citationCellRef = useRef<HTMLTableCellElement | null>(null);

  useEffect(() => {
    if (typeof activeSheetIndex === "number") {
      setLocalActiveSheet(activeSheetIndex);
    }
  }, [activeSheetIndex]);

  // Sheet the citation highlight points at: explicit index wins, then a
  // case-insensitive name match, then the current sheet (anchor with no
  // sheet info, e.g. a row-only tool citation).
  const highlightSheetIndex = useMemo(() => {
    if (!highlight || !workbook) return null;
    if (
      highlight.sheetIndex != null &&
      workbook.sheets[highlight.sheetIndex] != null
    ) {
      return highlight.sheetIndex;
    }
    const name = highlight.sheetName?.trim().toLowerCase();
    if (name) {
      const idx = workbook.sheets.findIndex(
        (s) => s.name.trim().toLowerCase() === name,
      );
      if (idx >= 0) return idx;
      return null;
    }
    return highlight.sheetIndex != null ? null : localActiveSheet;
  }, [highlight, workbook, localActiveSheet]);

  // Citation navigation: surface the cited sheet when a highlight lands.
  useEffect(() => {
    if (highlightSheetIndex == null) return;
    setLocalActiveSheet(highlightSheetIndex);
  }, [highlightSheetIndex]);

  const citationBounds = useMemo(() => {
    if (!highlight) return null;
    if (highlight.range) return parseRange(highlight.range);
    if (highlight.row != null && highlight.row > 0) {
      return {
        minCol: 0,
        minRow: highlight.row - 1,
        maxCol: Number.MAX_SAFE_INTEGER,
        maxRow: highlight.row - 1,
      };
    }
    return null;
  }, [highlight]);

  // Bring the cited cells into view once the highlight (and its sheet
  // switch) have rendered.
  useEffect(() => {
    if (!citationBounds) return;
    const id = requestAnimationFrame(() => {
      const el = citationCellRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [citationBounds, highlightSheetIndex, workbook]);

  useEffect(() => {
    let cancelled = false;
    const fetchAndParse = async () => {
      setError(null);
      setWorkbook(null);
      try {
        const resp = await fetch(url);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const buf = await resp.arrayBuffer();
        const XLSX = await import("xlsx");
        const wb = XLSX.read(buf, { type: "array", cellFormula: false });
        if (cancelled) return;
        const sheets: Sheet[] = wb.SheetNames.map((name: string) => {
          const ws = wb.Sheets[name];
          // sheet_to_json({header:1}) indexes rows/cols relative to the
          // sheet's used-range ORIGIN (!ref), but cell anchors and citation
          // ranges are absolute A1 (openpyxl on the backend). Force an
          // absolute A1 origin so a sheet whose data starts at e.g. B2 does
          // not shift every rendered column letter, row number, and anchored
          // cell one step left/up.
          const usedRange = ws?.["!ref"];
          const aoa = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            blankrows: true,
            defval: undefined,
            ...(usedRange
              ? {
                  range: {
                    s: { r: 0, c: 0 },
                    e: XLSX.utils.decode_range(usedRange).e,
                  },
                }
              : {}),
          }) as unknown[][];
          const truncated = aoa
            .slice(0, MAX_RENDER_ROWS)
            .map((row) => (row ?? []).slice(0, MAX_RENDER_COLS));
          // Grid width must cover the sheet's DECLARED used range, not just
          // the longest non-empty row: sheet_to_json drops trailing empty
          // cells per row, and a citation/anchor may point at an empty cell
          // past the last populated column (e.g. Y22 cited while the content
          // sits in X22 on a sheet declaring A1:BE275). A cell that is never
          // rendered has no DOM node to outline or scroll to, so the
          // highlight silently no-ops.
          const maxRowLen = truncated.reduce(
            (max, row) => Math.max(max, row.length),
            0,
          );
          const refCols = usedRange
            ? XLSX.utils.decode_range(usedRange).e.c + 1
            : 0;
          const colCount = Math.min(
            Math.max(maxRowLen, refCols),
            MAX_RENDER_COLS,
          );
          return { name, cells: truncated, colCount };
        });
        setWorkbook({ sheets });
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      }
    };
    fetchAndParse();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const overlaysBySheet = useMemo(() => {
    const map = new Map<
      number,
      {
        commentId: string;
        rangeStr: string;
        bounds: ReturnType<typeof parseRange>;
        description: string;
      }[]
    >();
    for (const c of comments ?? []) {
      if (!c.cellAnchor) continue;
      if (c.disposition === "dismissed") continue;
      const bounds = parseRange(c.cellAnchor.range);
      if (!bounds) continue;
      const list = map.get(c.cellAnchor.sheetIndex) ?? [];
      list.push({
        commentId: c._id ?? "",
        rangeStr: c.cellAnchor.range,
        bounds,
        description: c.description ?? "",
      });
      map.set(c.cellAnchor.sheetIndex, list);
    }
    return map;
  }, [comments]);

  const overlayKey = (sheetIdx: number, col: number, row: number) =>
    `${sheetIdx}:${col}:${row}`;

  const overlayCells = useMemo(() => {
    const set = new Map<string, { commentId: string; description: string }>();
    overlaysBySheet.forEach((list, sheetIdx) => {
      for (const o of list) {
        if (!o.bounds) continue;
        // Mirror the pin viewers: with a selection, only the active
        // comment's cells outline so stepping through comments visibly
        // moves the highlight. (Sheet-tab badges keep total counts.)
        if (activeCommentId && o.commentId !== activeCommentId) continue;
        for (let r = o.bounds.minRow; r <= o.bounds.maxRow; r++) {
          for (let c = o.bounds.minCol; c <= o.bounds.maxCol; c++) {
            set.set(overlayKey(sheetIdx, c, r), {
              commentId: o.commentId,
              description: o.description,
            });
          }
        }
      }
    });
    return set;
  }, [overlaysBySheet, activeCommentId]);

  // Top-left cell of the active comment's anchor — the scroll target.
  const activeOverlayCell = useMemo<{
    sheetIdx: number;
    col: number;
    row: number;
  } | null>(() => {
    if (!activeCommentId) return null;
    let found: { sheetIdx: number; col: number; row: number } | null = null;
    overlaysBySheet.forEach((list, sheetIdx) => {
      if (found) return;
      for (const o of list) {
        if (o.commentId !== activeCommentId || !o.bounds) continue;
        found = { sheetIdx, col: o.bounds.minCol, row: o.bounds.minRow };
        return;
      }
    });
    return found;
  }, [overlaysBySheet, activeCommentId]);

  // Bring the active comment's cells into view when selection changes
  // (mirrors the pin viewers' scroll-to-active-pin behavior).
  const activeCellRef = useRef<HTMLTableCellElement | null>(null);
  useEffect(() => {
    if (!activeCommentId) return;
    const id = requestAnimationFrame(() => {
      const el = activeCellRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [activeCommentId, localActiveSheet, workbook]);

  const handleSheetClick = (idx: number) => {
    setLocalActiveSheet(idx);
    onSheetChange?.(idx);
  };

  if (error) {
    return (
      <div className="p-4 text-puntt-neutral-gray-11">
        <p>Could not render spreadsheet: {error}</p>
        <a
          data-testid="spreadsheet-view-download-link"
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
        >
          {t("spreadsheetView.downloadInstead")}
        </a>
      </div>
    );
  }

  if (!workbook) {
    return <div className="p-4">{t("spreadsheetView.loadingSpreadsheet")}</div>;
  }

  const sheet = workbook.sheets[localActiveSheet] ?? workbook.sheets[0];
  if (!sheet) {
    return <div className="p-4">{t("spreadsheetView.noSheetsWorkbook")}</div>;
  }

  return (
    <div className="flex size-full flex-col">
      <div
        ref={containerRef}
        // No border-top: this view sits under pane/header chrome that already
        // draws a 1px rule. A full `border` stacked to a 2px line under headers.
        className="flex-1 overflow-auto border-x border-b border-puntt-neutral-gray-6"
      >
        {/*
          border-separate (not collapse): sticky row/col chrome is unreliable
          under border-collapse and paints cell text through frozen gutters.
          Freeze ONLY the letter header row + row-index gutter — not data col A
          (freezing A stacked "A" onto scrolled headers like "AG" → "AGA").
        */}
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th
                data-testid="spreadsheet-corner-cell"
                className="font-mono sticky left-0 top-0 z-30 w-10 min-w-10 border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-3 px-2 py-1 text-center text-xs"
              >
                &nbsp;
              </th>
              {Array.from({ length: sheet.colCount }).map((_, c) => (
                <th
                  key={c}
                  data-testid={c === 0 ? "spreadsheet-col-a-header" : undefined}
                  className="font-mono sticky top-0 z-20 border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-2 py-1 text-center text-xs"
                >
                  {colLetter(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.cells.map((row, rIdx) => (
              <tr key={rIdx}>
                <th
                  data-testid={`spreadsheet-row-index-${rIdx + 1}`}
                  className="font-mono sticky left-0 z-10 w-10 min-w-10 border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-3 px-2 py-1 text-center text-xs"
                >
                  {rIdx + 1}
                </th>
                {Array.from({ length: sheet.colCount }).map((_, cIdx) => {
                  const value = row[cIdx];
                  const overlay = overlayCells.get(
                    overlayKey(localActiveSheet, cIdx, rIdx),
                  );
                  const isCitation =
                    citationBounds != null &&
                    highlightSheetIndex === localActiveSheet &&
                    rIdx >= citationBounds.minRow &&
                    rIdx <= citationBounds.maxRow &&
                    cIdx >= citationBounds.minCol &&
                    cIdx <= citationBounds.maxCol;
                  const isFirstCitationCell =
                    isCitation &&
                    rIdx === citationBounds.minRow &&
                    cIdx === Math.max(citationBounds.minCol, 0);
                  const isActiveAnchorCell =
                    activeOverlayCell != null &&
                    activeOverlayCell.sheetIdx === localActiveSheet &&
                    rIdx === activeOverlayCell.row &&
                    cIdx === activeOverlayCell.col;
                  return (
                    <td
                      data-testid={
                        overlay
                          ? `spreadsheet-cell-comment-${colLetter(cIdx)}${rIdx + 1}`
                          : isCitation
                            ? `spreadsheet-cell-citation-${colLetter(cIdx)}${rIdx + 1}`
                            : cIdx === 0
                              ? `spreadsheet-cell-label-${rIdx + 1}`
                              : undefined
                      }
                      key={cIdx}
                      ref={
                        isFirstCitationCell
                          ? citationCellRef
                          : isActiveAnchorCell
                            ? activeCellRef
                            : undefined
                      }
                      title={overlay ? overlay.description : undefined}
                      onClick={
                        overlay && overlay.commentId
                          ? () => onCommentClick?.(overlay.commentId)
                          : undefined
                      }
                      className={cx(
                        "whitespace-nowrap border border-puntt-neutral-gray-6 bg-base-white px-2 py-1",
                        overlay &&
                          !isCitation &&
                          "cursor-pointer bg-puntt-accent-3 outline outline-2 outline-puntt-accent-9",
                        overlay && isCitation && "cursor-pointer",
                        // Citation ping styling — gold to match the document
                        // citation highlight ring in the asset viewer.
                        isCitation &&
                          "bg-puntt-gold-3 outline outline-2 outline-puntt-gold-9",
                      )}
                    >
                      {value === undefined || value === null
                        ? ""
                        : String(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {workbook.sheets.length > 1 && (
        <div className="flex gap-1 border-t border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-2 py-1">
          {workbook.sheets.map((s, idx) => {
            const overlayCount = overlaysBySheet.get(idx)?.length ?? 0;
            const isActive = idx === localActiveSheet;
            return (
              <button
                data-testid={`spreadsheet-sheet-tab-${idx}`}
                key={idx}
                type="button"
                onClick={() => handleSheetClick(idx)}
                className={cx(
                  "rounded border px-3 py-1 text-xs",
                  isActive
                    ? "border-puntt-accent-9 bg-puntt-accent-3 text-puntt-accent-12"
                    : "border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 hover:bg-puntt-neutral-gray-3",
                )}
              >
                {s.name}
                {overlayCount > 0 && (
                  <span className="text-puntt-neutral-1 ml-2 inline-block rounded-full bg-puntt-accent-9 px-1.5">
                    {overlayCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
